import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import {
  DataTable,
  DataTableFilterMeta,
  DataTableRowToggleEvent,
} from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { OverlayPanel } from "primereact/overlaypanel";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  IcmJobProgress,
  IcmStage,
  ReportJobBatchEntry,
  ReportJobEntry,
  WorkStatus,
} from "../../../../client";
import { LOB } from "../../../../api/routes";
import { useQueueJobs } from "../../rater/useIntegrationJobs";
import { ROUTES } from "../../../../routes";
import {
  useCancelJobs,
  useLocalUser,
  useSafeReports,
  useUsers,
} from "../../data";
import { isRunningLocally } from "../../../../util";
import styles from "./shared.module.css";
import { formatTimestamp } from "./submissionHistoryUtils";

type StatusLabel =
  | "In Progress"
  | "Pending"
  | "Partially Running"
  | "Blocked"
  | "Waiting to submit to ICM";

const ICM_STAGE_LABELS: Record<IcmStage, string> = {
  submitted: "submitted",
  cat_modeling: "cat modeling",
  scoring: "scoring",
  complete: "complete",
};

type TopicBreakdown = {
  topic: string;
  done: number;
  total: number;
};

type QueueRow = ReportJobBatchEntry & {
  company_name: string;
  triggered_by: string;
  min_position: number | null;
  position_display: string;
  status_label: StatusLabel;
  topic_breakdown: TopicBreakdown[];
  icm_progress: IcmJobProgress | null;
};

const TERMINAL_STATUSES: ReadonlySet<WorkStatus> = new Set<WorkStatus>([
  "done",
  "failed",
  "cancelled",
]);

const isTerminal = (status: WorkStatus) => TERMINAL_STATUSES.has(status);

const TYPE_OPTIONS = [
  "ADDRESS_INTEGRATION",
  "ICM",
  "POLICY_FLY",
  "RATER",
  "RERUN_REPORT",
  "RMS",
];

const STATUS_OPTIONS = [
  "In Progress",
  "Pending",
  "Partially Running",
  "Blocked",
  "Waiting to submit to ICM",
];

const PRODUCTION_ENV = "production";

const defaultFilters: DataTableFilterMeta = {
  integration_type: { value: null, matchMode: FilterMatchMode.EQUALS },
  status_label: { value: null, matchMode: FilterMatchMode.EQUALS },
  env: {
    value: isRunningLocally() ? null : PRODUCTION_ENV,
    matchMode: FilterMatchMode.EQUALS,
  },
};

const typeFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={TYPE_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Types"
    showClear
    style={{ minWidth: "150px" }}
  />
);

const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
  <Dropdown
    value={options.value}
    options={STATUS_OPTIONS}
    onChange={(e) => options.filterApplyCallback(e.value)}
    placeholder="All Statuses"
    showClear
    style={{ minWidth: "150px" }}
  />
);

const statusSeverity = (
  status: WorkStatus
): "success" | "info" | "warning" | "danger" => {
  switch (status) {
    case "done":
      return "success";
    case "in_progress":
      return "info";
    case "pending":
      return "warning";
    case "failed":
    case "cancelled":
      return "danger";
  }
};

// The API returns each batch's jobs in pipeline order (see
// task_dependencies.TOPIC_PIPELINE on the backend), so iterating in
// the received order — Map preserves insertion order — gives us the
// breakdown rows in the right sequence with a single pass.
const computeTopicBreakdown = (jobs: ReportJobEntry[]): TopicBreakdown[] => {
  const counts = new Map<string, { done: number; total: number }>();
  for (const job of jobs) {
    const cur = counts.get(job.topic) ?? { done: 0, total: 0 };
    cur.total += 1;
    if (isTerminal(job.status)) cur.done += 1;
    counts.set(job.topic, cur);
  }
  return [...counts.entries()].map(([topic, c]) => ({
    topic,
    done: c.done,
    total: c.total,
  }));
};

const TopicBreakdownDisplay = ({ rows }: { rows: TopicBreakdown[] }) => {
  if (rows.length === 0) return null;
  return (
    <div className={styles.topicBreakdown}>
      {rows.map((r) => {
        const fullyDone = r.done === r.total;
        return (
          <div
            key={r.topic}
            className={[
              styles.topicLine,
              fullyDone ? styles.topicLineDone : styles.topicLineActive,
            ].join(" ")}
          >
            <span>{r.topic}</span>
            <span>
              {r.done}/{r.total}
            </span>
          </div>
        );
      })}
    </div>
  );
};

const ResponseIcon = ({
  response,
}: {
  response: ReportJobEntry["response"];
}) => {
  const overlayPanel = useRef<OverlayPanel>(null);
  if (response == null) return <span>—</span>;
  return (
    <>
      <Button
        icon="pi pi-file"
        text
        rounded
        size="small"
        className={styles.responseButton}
        aria-label="View response"
        onClick={(e) => overlayPanel.current?.toggle(e)}
      />
      <OverlayPanel ref={overlayPanel} showCloseIcon dismissable>
        <pre
          style={{
            margin: 0,
            maxWidth: "600px",
            maxHeight: "400px",
            overflow: "auto",
            fontSize: "0.75rem",
          }}
        >
          {JSON.stringify(response, null, 2)}
        </pre>
      </OverlayPanel>
    </>
  );
};

export const JobsInQueue = () => {
  const user = useLocalUser();
  const lobsForUser = (user?.lobs ?? []) as LOB[];
  const reports = useSafeReports(lobsForUser).data ?? [];
  const allUsers = useUsers().data?.all_company_users ?? [];
  const cancelJobs = useCancelJobs();

  const [filters, setFilters] = useState<DataTableFilterMeta>(defaultFilters);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const reportIds = useMemo(() => reports.map((r) => r.report_id), [reports]);

  const companyNameByReportId = useMemo(
    () =>
      Object.fromEntries(
        reports.map((r) => [
          r.report_id,
          r.report_metadata.company_info.company_name,
        ])
      ),
    [reports]
  );

  const userNameById = useMemo(
    () =>
      Object.fromEntries(
        allUsers.map((u) => [u.id, u.full_name?.trim() || u.email])
      ),
    [allUsers]
  );

  const { data: jobs = [], isLoading } = useQueueJobs(reportIds, [
    "pending",
    "in_progress",
  ]);

  const rows: QueueRow[] = useMemo(() => {
    return jobs.map((job) => {
      const activeJobs = job.jobs.filter(
        (j) => j.status === "pending" || j.status === "in_progress"
      );
      const pendingPositions = activeJobs
        .map((j) => j.queue_position)
        .filter((p): p is number => p != null);
      const runningCount = activeJobs.filter(
        (j) => j.status === "in_progress"
      ).length;

      const min =
        pendingPositions.length > 0 ? Math.min(...pendingPositions) : null;
      const max =
        pendingPositions.length > 0 ? Math.max(...pendingPositions) : null;

      const position_display: string =
        pendingPositions.length === 0
          ? "—"
          : min === max
            ? `${min}`
            : `${min}–${max}`;

      const isBlocked =
        pendingPositions.length > 0 && (job.blocked_by_kinds ?? []).length > 0;

      const icmExportJob = activeJobs.find((j) => j.icm_progress != null);
      const icmProgress = icmExportJob?.icm_progress ?? null;
      const icmWaitingForSlot =
        icmExportJob?.status === "in_progress" &&
        icmProgress != null &&
        !icmProgress.submitted;

      const status_label: StatusLabel = icmWaitingForSlot
        ? "Waiting to submit to ICM"
        : runningCount > 0 && pendingPositions.length > 0
          ? "Partially Running"
          : pendingPositions.length === 0
            ? "In Progress"
            : isBlocked
              ? "Blocked"
              : "Pending";

      const triggered_by = job.initiating_user_id
        ? (userNameById[job.initiating_user_id] ?? job.initiating_user_id)
        : "—";

      return {
        ...job,
        company_name: companyNameByReportId[job.report_id] ?? "",
        triggered_by,
        min_position: min,
        position_display,
        status_label,
        topic_breakdown: computeTopicBreakdown(job.jobs),
        icm_progress: icmProgress,
      };
    });
  }, [jobs, companyNameByReportId, userNameById]);

  const envOptions = useMemo(
    () => Array.from(new Set(rows.map((r) => r.env))).sort(),
    [rows]
  );

  const envFilterTemplate = (options: ColumnFilterElementTemplateOptions) => (
    <Dropdown
      value={options.value}
      options={envOptions}
      onChange={(e) => options.filterApplyCallback(e.value)}
      placeholder="All Envs"
      showClear
      style={{ minWidth: "150px" }}
    />
  );

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        // in_progress (null position) before pending (has position)
        if (a.min_position == null && b.min_position != null) return -1;
        if (a.min_position != null && b.min_position == null) return 1;
        if (a.min_position != null && b.min_position != null)
          return a.min_position - b.min_position;
        // both null (in_progress) — sort by started_at
        return a.started_at.localeCompare(b.started_at);
      }),
    [rows]
  );

  // Compact DataTable expansion. The visual density is driven by CSS
  // overrides scoped under .expansionTable (see shared.module.css) —
  // smaller font, tighter cell padding, lighter header weight — so it
  // reads as a sub-listing rather than another full DataTable.
  const rowExpansionTemplate = (row: QueueRow) => (
    <div className={`${styles.expansionWrap} ${styles.expansionTable}`}>
      <DataTable value={row.jobs} dataKey="report_integration_id" size="small">
        <Column field="topic" header="Topic" />
        <Column
          header="Status"
          body={(job: ReportJobEntry) => (
            <Tag value={job.status} severity={statusSeverity(job.status)} />
          )}
        />
        <Column
          header="ICM"
          body={(job: ReportJobEntry) => {
            const p = job.icm_progress;
            if (p == null) return "—";
            if (!p.submitted) return "waiting to submit";
            const stage =
              p.stage != null ? ICM_STAGE_LABELS[p.stage] : "submitted";
            return p.location_count != null
              ? `${stage} · ${p.location_count.toLocaleString()} locations`
              : stage;
          }}
        />
        <Column
          header="Position"
          body={(job: ReportJobEntry) =>
            job.queue_position != null ? `#${job.queue_position}` : "—"
          }
        />
        <Column
          header="Started"
          body={(job: ReportJobEntry) => formatTimestamp(job.started_at)}
        />
        <Column
          header="Response"
          body={(job: ReportJobEntry) => (
            <ResponseIcon response={job.response} />
          )}
        />
        <Column
          header=""
          body={(job: ReportJobEntry) => {
            const isCancellingThis =
              cancelJobs.isPending &&
              Array.isArray(cancelJobs.variables) &&
              cancelJobs.variables.includes(job.report_integration_id);
            return (
              <Button
                label="Cancel"
                severity="warning"
                size="small"
                text
                className={styles.jobCancelButton}
                disabled={isTerminal(job.status) || cancelJobs.isPending}
                loading={isCancellingThis}
                onClick={() => cancelJobs.mutate([job.report_integration_id])}
              />
            );
          }}
        />
      </DataTable>
    </div>
  );

  return (
    <div className={styles.page}>
      <Card title="Jobs in Queue">
        {isLoading ? (
          <div className={styles.centered}>
            <ProgressSpinner />
          </div>
        ) : (
          <div className={styles.queueTable}>
            <DataTable
              value={sortedRows}
              filters={filters}
              filterDisplay="menu"
              onFilter={(e) => setFilters(e.filters)}
              emptyMessage="No jobs in queue"
              scrollable
              scrollHeight="calc(100vh - 300px)"
              stripedRows
              dataKey="batch_id"
              expandedRows={expandedRows}
              onRowToggle={(e: DataTableRowToggleEvent) =>
                setExpandedRows(e.data as Record<string, boolean>)
              }
              rowExpansionTemplate={rowExpansionTemplate}
              rowClassName={(row: QueueRow) =>
                expandedRows[row.batch_id] ? styles.expandedParent : ""
              }
            >
              <Column expander style={{ width: "3rem" }} />
              <Column
                field="env"
                header="Env"
                filter
                filterElement={envFilterTemplate}
                showFilterMatchModes={false}
                sortable
                style={{ width: "160px" }}
              />
              <Column
                field="min_position"
                header="Position"
                sortable
                style={{ width: "100px" }}
                body={(row: QueueRow) => row.position_display}
              />
              <Column
                field="status_label"
                header="Status"
                filter
                filterElement={statusFilterTemplate}
                showFilterMatchModes={false}
                sortable
                style={{ width: "240px" }}
                body={(row: QueueRow) => (
                  <div className={styles.statusCell}>
                    <Tag
                      value={row.status_label}
                      severity={
                        row.status_label === "Pending" ||
                        row.status_label === "Partially Running" ||
                        row.status_label === "Waiting to submit to ICM"
                          ? "warning"
                          : row.status_label === "Blocked"
                            ? "danger"
                            : "info"
                      }
                    />
                    {row.status_label === "Blocked" &&
                      row.blocked_by_kinds.length > 0 && (
                        <span className={styles.stageDisplay}>
                          waiting on {row.blocked_by_kinds.join(", ")}
                        </span>
                      )}
                    {row.status_label === "Waiting to submit to ICM" && (
                      <span className={styles.stageDisplay}>
                        queued behind active ICM rating
                      </span>
                    )}
                    {row.icm_progress?.submitted &&
                      row.icm_progress.stage != null && (
                        <span className={styles.stageDisplay}>
                          ICM: {ICM_STAGE_LABELS[row.icm_progress.stage]}
                          {row.icm_progress.location_count != null &&
                            ` · ${row.icm_progress.location_count.toLocaleString()} locations`}
                        </span>
                      )}
                    <TopicBreakdownDisplay rows={row.topic_breakdown} />
                  </div>
                )}
              />
              <Column
                field="integration_type"
                header="Type"
                filter
                filterElement={typeFilterTemplate}
                showFilterMatchModes={false}
                sortable
                style={{ width: "200px" }}
              />
              <Column
                field="company_name"
                header="Company"
                sortable
                style={{ minWidth: "200px" }}
                body={(row: QueueRow) => (
                  <Link to={ROUTES.DASHBOARD_REPORT(row.report_id)}>
                    {row.company_name}
                  </Link>
                )}
              />
              <Column
                field="triggered_by"
                header="Triggered By"
                sortable
                style={{ width: "200px" }}
              />
              <Column
                field="started_at"
                header="Started"
                sortable
                style={{ width: "200px" }}
                body={(row: QueueRow) => formatTimestamp(row.started_at)}
              />
              <Column
                header="Jobs"
                style={{ width: "80px" }}
                body={(row: QueueRow) => row.jobs.length}
              />
              <Column
                header=""
                style={{ width: "120px" }}
                alignFrozen="right"
                frozen
                body={(row: QueueRow) => {
                  const activeIds = row.jobs
                    .filter(
                      (j) =>
                        j.status === "pending" || j.status === "in_progress"
                    )
                    .map((j) => j.report_integration_id);
                  const isCancellingThis =
                    cancelJobs.isPending &&
                    Array.isArray(cancelJobs.variables) &&
                    cancelJobs.variables.some((id) => activeIds.includes(id));
                  return (
                    <Button
                      label="Cancel"
                      severity="warning"
                      size="small"
                      disabled={activeIds.length === 0 || cancelJobs.isPending}
                      loading={isCancellingThis}
                      onClick={() => cancelJobs.mutate(activeIds)}
                    />
                  );
                }}
              />
            </DataTable>
          </div>
        )}
      </Card>
    </div>
  );
};
